import { jsx } from 'react/jsx-runtime';
import { createGlobalReferenceContext } from '@flex-packages/shared-react';
import { useContext, useState, useEffect, useMemo } from 'react';
import { amplitudeLogger } from '../logger/amplitude.mjs';
import { devLogger } from '../utils/devLogger.mjs';
import { getDeviceIdFromUrlParam } from '../utils/getDeviceIdFromUrlParam.mjs';
import useEventCallback from '../utils/useEventCallback.mjs';
import useReferenceSafeObject from '../utils/useReferenceSafeObject.mjs';

const LoggerContext = createGlobalReferenceContext("logger-context", void 0);
const LoggerContextProvider = ({
  enabled,
  apiKey,
  children,
  referrerUrl,
  serverUrl,
  keepAlive = false,
  flexUTMs: rawFlexUTMs,
  onInitializeError,
  userProperties: rawUserProperties,
  defaultEventProperties: rawDefaultEventProperties,
  options: { dryRun = false, deviceIdFromUrlParam = false } = {}
}) => {
  const [logger] = useState(amplitudeLogger);
  const userProperties = useReferenceSafeObject(rawUserProperties);
  const defaultEventProperties = useReferenceSafeObject(
    rawDefaultEventProperties
  );
  const flexUTMs = useReferenceSafeObject(rawFlexUTMs);
  const initializeLogger = useEventCallback((_apiKey) => {
    logger.config({ dryRun });
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    const browserSizeConfig = {
      browserWidth: windowWidth,
      browserHeight: windowHeight
    };
    logger.initialize(
      _apiKey,
      { ...userProperties, ...browserSizeConfig },
      {
        keepAlive,
        serverUrl,
        deviceId: deviceIdFromUrlParam ? getDeviceIdFromUrlParam(window.location.search) : void 0,
        onError: onInitializeError
      }
    );
  });
  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (typeof apiKey !== "string" || apiKey.trim().length === 0) {
      devLogger.warn("apiKey\uAC00 \uC720\uD6A8\uD558\uC9C0 \uC54A\uC2B5\uB2C8\uB2E4: ", apiKey);
      return;
    }
    initializeLogger(apiKey);
  }, [apiKey, enabled, initializeLogger]);
  useEffect(() => {
    logger.setUserProperties(userProperties);
  }, [logger, userProperties]);
  return /* @__PURE__ */ jsx(
    LoggerContext.Provider,
    {
      value: useMemo(
        () => ({ logger, defaultEventProperties, referrerUrl, flexUTMs }),
        [defaultEventProperties, logger, referrerUrl, flexUTMs]
      ),
      children
    }
  );
};
const useLoggerContext = () => {
  const context = useContext(LoggerContext);
  if (!context) {
    throw new Error("LoggerContextProvider \uC640 \uD568\uAED8 \uC0AC\uC6A9\uD574\uC8FC\uC138\uC694.");
  }
  return context;
};

export { LoggerContext, LoggerContextProvider, useLoggerContext };
//# sourceMappingURL=logger.mjs.map
